
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

export const DeliveriesTab = () => {
  const isMobile = useIsMobile();
  const { data: deliveries, isLoading } = useQuery({
    queryKey: ["deliveries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales")
        .select(`
          *,
          delivery_zones (
            name,
            price
          ),
          delivery_drivers (
            name,
            contact
          ),
          sale_items (
            quantity,
            unit_price,
            total_price,
            stock_items (
              data
            )
          )
        `)
        .eq("is_delivery", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      pending: { variant: "secondary", label: "En attente" },
      in_progress: { variant: "default", label: "En cours" },
      delivered: { variant: "outline", label: "Livrée" },
      cancelled: { variant: "destructive", label: "Annulée" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const renderMobileDelivery = (delivery: any) => (
    <TableRow key={delivery.id}>
      <TableCell>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div>{format(new Date(delivery.created_at), "Pp", { locale: fr })}</div>
            <div>{delivery.customer_name}</div>
            {delivery.customer_contact && (
              <div className="text-sm text-muted-foreground">
                {delivery.customer_contact}
              </div>
            )}
            <div>{getDeliveryStatusBadge(delivery.delivery_status)}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="h-5 w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Livreur</div>
                <div>{delivery.delivery_drivers?.name || delivery.custom_driver_name || "-"}</div>
              </DropdownMenuItem>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Zone</div>
                <div>{delivery.delivery_zones?.name || "-"}</div>
                {delivery.delivery_address && (
                  <div className="text-sm text-muted-foreground">
                    {delivery.delivery_address}
                  </div>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Produits</div>
                {delivery.sale_items?.map((item: any) => (
                  <div key={item.id}>
                    {item.quantity}x {item.stock_items.data.name}
                  </div>
                ))}
              </DropdownMenuItem>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Montant produits</div>
                <div>{delivery.total_amount?.toLocaleString()} FCFA</div>
              </DropdownMenuItem>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Frais livraison</div>
                <div>
                  {(delivery.custom_delivery_amount || delivery.delivery_zones?.price || 0).toLocaleString()} FCFA
                  {delivery.delivery_payment_type === "direct" && (
                    <div className="text-sm text-muted-foreground">
                      (À payer au livreur)
                    </div>
                  )}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem className="space-y-1">
                <div className="font-medium">Total</div>
                <div>
                  {(delivery.total_amount + (delivery.custom_delivery_amount || delivery.delivery_zones?.price || 0)).toLocaleString()} FCFA
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderDesktopDelivery = (delivery: any) => (
    <TableRow key={delivery.id}>
      <TableCell>
        {format(new Date(delivery.created_at), "Pp", { locale: fr })}
      </TableCell>
      <TableCell>
        <div>{delivery.customer_name}</div>
        {delivery.customer_contact && (
          <div className="text-sm text-muted-foreground">
            {delivery.customer_contact}
          </div>
        )}
      </TableCell>
      <TableCell>
        {delivery.delivery_drivers?.name || delivery.custom_driver_name || "-"}
      </TableCell>
      <TableCell>
        <div>{delivery.delivery_zones?.name || "-"}</div>
        {delivery.delivery_address && (
          <div className="text-sm text-muted-foreground">
            {delivery.delivery_address}
          </div>
        )}
      </TableCell>
      <TableCell>
        {delivery.sale_items?.map((item: any) => (
          <div key={item.id}>
            {item.quantity}x {item.stock_items.data.name}
          </div>
        ))}
      </TableCell>
      <TableCell>
        {delivery.total_amount?.toLocaleString()} FCFA
      </TableCell>
      <TableCell>
        {(delivery.custom_delivery_amount || delivery.delivery_zones?.price || 0).toLocaleString()} FCFA
        {delivery.delivery_payment_type === "direct" && (
          <div className="text-sm text-muted-foreground">
            (À payer au livreur)
          </div>
        )}
      </TableCell>
      <TableCell>
        {(delivery.total_amount + (delivery.custom_delivery_amount || delivery.delivery_zones?.price || 0)).toLocaleString()} FCFA
      </TableCell>
      <TableCell>
        {getDeliveryStatusBadge(delivery.delivery_status)}
      </TableCell>
    </TableRow>
  );

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  return (
    <Card>
      <Table>
        {!isMobile ? (
          <>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Livreur</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Produits</TableHead>
                <TableHead>Montant produits</TableHead>
                <TableHead>Frais livraison</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries?.map(renderDesktopDelivery)}
            </TableBody>
          </>
        ) : (
          <TableBody>
            {deliveries?.map(renderMobileDelivery)}
          </TableBody>
        )}
      </Table>
    </Card>
  );
};
